/**
 * Google Consent Mode v2 — shared constants and helpers.
 *
 * Region scoping: EU27 + EEA (IS/LI/NO) + UK (UK GDPR) + CH (revised FADP,
 * in force since Sep 2023 — Google recommends including CH in consent scoping).
 * The gtag `region` parameter uses Google's own server-side geo detection, which
 * is authoritative for tag behavior. The banner-display heuristic below is a
 * separate, deliberately over-inclusive client-side signal (UX only).
 */

export const CONSENT_COOKIE = '_tripening_consent'
export const CONSENT_COOKIE_DAYS = 180
export const CONSENT_EVENT = 'tripening:consent'

export const REGULATED_REGIONS = [
  // EU27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA (non-EU)
  'IS', 'LI', 'NO',
  // UK GDPR
  'GB',
  // Switzerland (revised FADP)
  'CH',
]

export type ConsentChoice = 'granted' | 'denied'

export function readConsentChoice(): ConsentChoice | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)_tripening_consent=([^;]+)/)
  const value = match ? decodeURIComponent(match[1]) : null
  return value === 'granted' || value === 'denied' ? value : null
}

export function writeConsentChoice(choice: ConsentChoice) {
  const expires = new Date()
  expires.setTime(expires.getTime() + CONSENT_COOKIE_DAYS * 24 * 60 * 60 * 1000)
  document.cookie = `${CONSENT_COOKIE}=${choice}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}

/**
 * Cheap client-side signal for "user is probably in a regulated region", used
 * ONLY to decide whether to show the banner. Over-inclusive by design: the
 * Europe/* timezone prefix also matches non-EEA countries (TR, RS, UA, RU…),
 * and Atlantic islands cover IS + ES/PT territories. Showing the banner to a
 * few extra users is harmless; the gtag `region` scoping (Google geo) is what
 * actually governs tag behavior. Known imprecision: VPNs and travelers.
 */
export function isLikelyRegulatedRegion(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    return (
      tz.startsWith('Europe/') ||
      tz === 'Atlantic/Reykjavik' ||
      tz === 'Atlantic/Canary' ||
      tz === 'Atlantic/Madeira' ||
      tz === 'Atlantic/Azores'
    )
  } catch {
    // If the runtime can't tell us, err on the side of asking.
    return true
  }
}

/** Push a consent update onto the shared dataLayer (gtag + GTM both read it). */
export function pushConsentUpdate(choice: ConsentChoice) {
  const w = window as any
  w.dataLayer = w.dataLayer || []
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments)
  }
  gtag('consent', 'update', {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  })
}
